import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  X, TrendingUp, ShoppingBag, Users, DollarSign, Trophy, Loader2,
  ArrowUpRight, ArrowDownRight, AlertTriangle, GitCompareArrows,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from 'recharts';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

/**
 * Full-screen "Performance" overlay launched from the POS today-tile.
 *
 * Re-uses the same `/api/cashier/today-tile` endpoint with a `period`
 * query param (today | week | month | year). Renders:
 *  - 4 KPI cards (orders, revenue, AOV, active cashiers)
 *  - Time-series line chart (revenue per bucket: hour/day/month)
 *  - Top-5 items bar chart
 *  - Payment-mix breakdown
 *
 * Polls every 10s while open so managers can leave it on the back-office
 * screen as a live wallboard during peak hours.
 */
export default function PerformanceOverlay({ open, onClose, branchId, cashierToken, currencySymbol = 'SR' }) {
  const [period, setPeriod] = useState('today');
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      setLoading(true);
      try {
        const headers = cashierToken ? { Authorization: `Bearer ${cashierToken}` } : {};
        const params = {
          period,
          ...(compare ? { compare: 1 } : {}),
          ...(branchId ? { branch_id: branchId } : {}),
        };
        const r = await api.get('/cashier/today-tile', { headers, params });
        if (!cancelled) setData(r.data);
      } catch {
        // Silent — overlay will show "—" placeholders
      } finally {
        if (!cancelled) setLoading(false);
        if (!cancelled) timer = setTimeout(tick, 10000);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [open, period, compare, branchId, cashierToken]);

  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const breakdown = data?.breakdown || [];
  const topItems = data?.top_items || [];
  const lossItems = data?.loss_items || [];
  const paymentMix = data?.payment_mix || [];
  const previous = data?.previous;

  // Merge current + previous breakdowns into one chart-friendly array.
  // We line them up bucket-by-bucket so the previous-period line overlays
  // the current one (e.g. "Tuesday this week vs Tuesday last week").
  const chartData = breakdown.map((b, i) => ({
    label: b.label,
    revenue: b.revenue,
    orders: b.orders,
    prev_revenue: previous?.breakdown?.[i]?.revenue ?? null,
  }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-[1200px] w-[96vw] max-h-[92dvh] overflow-y-auto p-0 rounded-2xl"
        data-testid="performance-overlay"
      >
        {/* iter427 — a11y title for screen readers; visible header below
            is the marketing label, not a Radix DialogTitle. */}
        <DialogHeader className="sr-only">
          <DialogTitle>Performance overlay</DialogTitle>
        </DialogHeader>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <TrendingUp size={18} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-outfit">Performance</h2>
              <p className="text-[11px] text-muted-foreground">
                {PERIODS.find(p => p.key === period)?.label} · live · refreshes every 10s
                {loading && <Loader2 size={10} className="inline-block ml-1.5 animate-spin" />}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Period tabs */}
            <div className="inline-flex rounded-xl bg-white dark:bg-stone-800 border p-0.5">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  data-testid={`perf-period-${p.key}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${
                    period === p.key
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Compare-to-last-period toggle. Reveals delta chips on
                KPI cards, overlays last-period revenue on the chart in
                grey, and surfaces the loss/low-margin items table. */}
            <button
              type="button"
              onClick={() => setCompare(c => !c)}
              data-testid="perf-compare-toggle"
              title="Compare to previous period (orders/revenue deltas + loss items)"
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${
                compare
                  ? 'bg-violet-500 text-white border-violet-600 shadow-sm'
                  : 'bg-white dark:bg-stone-800 text-muted-foreground border-stone-200 hover:text-foreground'
              }`}
            >
              <GitCompareArrows size={12} /> Compare
            </button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="perf-close" className="h-8 w-8 p-0">
              <X size={16} />
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 sm:p-6">
          <KpiCard icon={<DollarSign size={16} />} label="Revenue" value={`${currencySymbol} ${fmt(data?.revenue)}`} tone="emerald" testid="perf-kpi-revenue"
            delta={previous?.delta_revenue_pct} prevValue={previous ? `${currencySymbol} ${fmt(previous.revenue)}` : null} />
          <KpiCard icon={<ShoppingBag size={16} />} label="Orders" value={fmt(data?.orders)} tone="sky" testid="perf-kpi-orders"
            delta={previous?.delta_orders_pct} prevValue={previous ? fmt(previous.orders) : null} />
          <KpiCard icon={<TrendingUp size={16} />} label="Avg / Order" value={`${currencySymbol} ${fmt(data?.avg_order_value)}`} tone="violet" testid="perf-kpi-aov"
            delta={previous?.delta_aov_pct} prevValue={previous ? `${currencySymbol} ${fmt(previous.avg_order_value)}` : null} />
          <KpiCard icon={<Users size={16} />} label="On Shift" value={fmt(data?.active_cashiers)} tone="amber" testid="perf-kpi-shifts" />
        </div>

        {/* Time-series chart */}
        <div className="px-4 sm:px-6 pb-2">
          <div className="rounded-xl border bg-white dark:bg-stone-900/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">
                Revenue {data?.bucket_unit === 'hour' ? 'by hour' : data?.bucket_unit === 'day' ? 'by day' : 'by month'}
              </h3>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {breakdown.length} {data?.bucket_unit || 'point'}{breakdown.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="h-56" data-testid="perf-revenue-chart">
              {breakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,113,108,0.15)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#78716c' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#78716c' }} tickFormatter={(v) => fmt(v)} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 8, fontSize: 12 }}
                      formatter={(v, k) => {
                        if (v === null || v === undefined) return ['—', k];
                        if (k === 'revenue' || k === 'prev_revenue') return [`${currencySymbol} ${fmt(v)}`, k === 'prev_revenue' ? 'Previous' : 'Revenue'];
                        return [v, k];
                      }}
                    />
                    {compare && previous && (
                      <Line type="monotone" dataKey="prev_revenue" stroke="#a8a29e" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                    )}
                    <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="orders" stroke="#0ea5e9" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No orders yet for this period</div>
              )}
            </div>
          </div>
        </div>

        {/* Top items + payment mix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 sm:p-6 pt-2">
          <div className="lg:col-span-2 rounded-xl border bg-white dark:bg-stone-900/40 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-amber-600" />
              <h3 className="text-sm font-semibold">Top 5 items</h3>
            </div>
            <div className="h-48" data-testid="perf-top-items">
              {topItems.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topItems} layout="vertical" margin={{ top: 5, right: 12, left: 60, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,113,108,0.15)" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#78716c' }} tickFormatter={(v) => fmt(v)} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#44403c' }} width={120} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 8, fontSize: 12 }}
                      formatter={(v, k) => k === 'revenue' ? `${currencySymbol} ${fmt(v)}` : v}
                    />
                    <Bar dataKey="revenue" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No items sold in this period</div>
              )}
            </div>
          </div>

          {/* Payment mix */}
          <div className="rounded-xl border bg-white dark:bg-stone-900/40 p-4">
            <h3 className="text-sm font-semibold mb-3">Payment mix</h3>
            <div className="space-y-2" data-testid="perf-payment-mix">
              {paymentMix.length === 0 && (
                <p className="text-xs text-muted-foreground">No payments yet</p>
              )}
              {paymentMix.map(p => {
                const pct = data?.revenue ? (p.revenue / data.revenue) * 100 : 0;
                return (
                  <div key={p.method} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="capitalize font-medium">{p.method}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {currencySymbol} {fmt(p.revenue)} · {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Loss / low-margin items — only when comparing. Surfaces items
            where revenue ≤ cost or margin < 15% so managers can act on
            menu pricing or recipe cost overruns. */}
        {compare && (
          <div className="px-4 sm:px-6 pb-6" data-testid="perf-loss-section">
            <div className="rounded-xl border border-rose-200 dark:border-rose-900/30 bg-rose-50/40 dark:bg-rose-950/10 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-rose-600" />
                <h3 className="text-sm font-semibold text-rose-800 dark:text-rose-300">Loss / low-margin items</h3>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  Threshold: profit ≤ 0 OR margin &lt; 15%
                </span>
              </div>
              {lossItems.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 text-center">
                  No items losing money this period — every dish with a known cost is profitable. ✓
                </p>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs" data-testid="perf-loss-table">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-rose-200/50">
                        <th className="px-3 py-2 font-medium">Item</th>
                        <th className="px-3 py-2 font-medium text-right">Qty</th>
                        <th className="px-3 py-2 font-medium text-right">Revenue</th>
                        <th className="px-3 py-2 font-medium text-right">Cost</th>
                        <th className="px-3 py-2 font-medium text-right">Profit</th>
                        <th className="px-3 py-2 font-medium text-right">Margin</th>
                        <th className="px-3 py-2 font-medium text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lossItems.map((it) => {
                        // Recommended minimum-profit price: unit_cost × 1.3
                        // (30% target margin). Pre-filled into the editor
                        // banner so managers can re-price in one click.
                        const suggested = (it.unit_cost || (it.qty ? it.cost / it.qty : 0)) * 1.3;
                        const editUrl = it.menu_item_id
                          ? `/menu-items?edit=${it.menu_item_id}&suggest_price=${suggested.toFixed(2)}`
                          : null;
                        return (
                          <tr key={it.name} className="border-b border-rose-100/40 hover:bg-rose-100/30">
                            <td className="px-3 py-2 font-medium">{it.name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{it.qty}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{currencySymbol} {fmt(it.revenue)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{currencySymbol} {fmt(it.cost)}</td>
                            <td className={`px-3 py-2 text-right font-semibold tabular-nums ${it.profit < 0 ? 'text-rose-600' : 'text-amber-600'}`}>
                              {it.profit < 0 ? '−' : ''}{currencySymbol} {fmt(Math.abs(it.profit))}
                            </td>
                            <td className={`px-3 py-2 text-right font-semibold tabular-nums ${it.margin_pct < 0 ? 'text-rose-600' : 'text-amber-600'}`}>
                              {it.margin_pct.toFixed(1)}%
                            </td>
                            <td className="px-3 py-2 text-right">
                              {editUrl ? (
                                <a
                                  href={editUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200 transition-colors"
                                  data-testid={`perf-suggest-price-${it.menu_item_id}`}
                                  title={`Open editor with suggested price ${currencySymbol} ${suggested.toFixed(2)} (cost × 1.3)`}
                                >
                                  Suggest {currencySymbol} {suggested.toFixed(2)}
                                </a>
                              ) : (
                                <span className="text-muted-foreground/50 text-[10px]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-2">
                Items without <code>cost_price</code> set on their menu record are excluded.
                Update costs under Menu Items to make every dish trackable.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ icon, label, value, tone, testid, delta = null, prevValue = null }) {
  const toneMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20',
    sky: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/20',
    violet: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/20',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20',
  };
  const showDelta = delta !== null && delta !== undefined;
  const deltaPositive = showDelta && delta > 0;
  const deltaNegative = showDelta && delta < 0;
  return (
    <div className={`rounded-xl border p-3 ${toneMap[tone] || ''}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold opacity-80">
        {icon}<span>{label}</span>
      </div>
      <p className="font-bold text-xl mt-1 tabular-nums">{value}</p>
      {showDelta ? (
        <div
          className={`mt-1 inline-flex items-center gap-0.5 text-[10px] font-bold rounded-md px-1.5 py-0.5 ${
            deltaPositive ? 'bg-emerald-100/70 text-emerald-700' : deltaNegative ? 'bg-rose-100/70 text-rose-700' : 'bg-stone-100 text-stone-500'
          }`}
          title={prevValue ? `Previous: ${prevValue}` : ''}
          data-testid={`${testid}-delta`}
        >
          {deltaPositive ? <ArrowUpRight size={10} /> : deltaNegative ? <ArrowDownRight size={10} /> : null}
          {Math.abs(delta).toFixed(1)}%
          <span className="font-normal opacity-70 ml-0.5">vs prev</span>
        </div>
      ) : prevValue === null && delta === null ? null : (
        <div className="mt-1 text-[10px] text-muted-foreground/70">— no prior data</div>
      )}
    </div>
  );
}
